export interface ILanguageConfig {
    default: string;
    available: string[];
    definedCanonicalLanguage: string;
}

export type ICanonicalTagPostProcess = (inputUrl: string) => string;

/**
 * The Linktag service helps in setting link hreflang tags used for geotargeting
 */
export class LinktagService {
    private languagesConfig: ILanguageConfig | undefined;
    private singleLanguage = false;

    /**
     * Specifies the language configuration used to generate the HrefLang tags
     *
     * @param config the language configuration as defined in the config.json file
     */
    setLanguagesConfig(config: ILanguageConfig) {
        this.languagesConfig = config;
        this.singleLanguage = config.available.length === 1;
    }

    /**
     * When invoked, it computes the canonical url and sets it to the html head
     * The url is computed based on:
     * - the relative path given (e.g. "/en/games/live-casino")
     * - the currently selected language
     *
     * @param urlPath the current url path
     * @param postProcess an optional post process function applied to the final canonical Url before injecting it in the html
     */
    setCanonicalTagFromPath(
        urlPath: string,
        postProcess?: ICanonicalTagPostProcess,
    ) {
        if (!this.languagesConfig) {
            return;
        }
        const availableLangs = this.languagesConfig.available;
        const defaultLang = this.languagesConfig.default;
        const currentLang = this.getCurrentLang(
            urlPath,
            availableLangs,
            defaultLang,
        );

        const canonicalUrl = this.createUrl(urlPath, currentLang);
        return this.setCanonicalTagFromUrl(
            postProcess ? postProcess(canonicalUrl) : canonicalUrl,
        );
    }

    /**
     * When invoked, it sets/creates the needed HrefLang tag
     * setting the href attribute to the value specified
     *
     * @param canonicalUrl the url to set as canonical
     */
    setCanonicalTagFromUrl(canonicalUrl: string) {
        let tag = this.getCanonicalTag();
        if (!tag) {
            const createdTag = document.createElement('link');
            createdTag.setAttribute('rel', 'canonical');
            tag = document.head.appendChild(createdTag);
        }

        tag.setAttribute('href', canonicalUrl);

        return tag;
    }

    /**
     * Retrieves current language from the url if it is in the list of
     * supported langs, otherwise default language is returned
     * @param url the current url
     * @param langs list of supported languages
     * @param defaultLang default language
     * @returns the current language or default language
     */
    private getCurrentLang(url: string, langs: string[], defaultLang: string) {
        const currentLangRegexp = new RegExp(`^\/(${langs.join('|')})`);
        const matches = url.match(currentLangRegexp);
        return (matches && matches[1]) || defaultLang;
    }

    private getCanonicalTag() {
        return document.querySelector("link[rel='canonical']");
    }

    private createUrl(urlPath: string, lang: string) {
        const origin = window.location.origin;
        const slashIndex = urlPath.indexOf('/', 1);

        if (this.singleLanguage) {
            return `${origin}${urlPath}`;
        } else {
            const subIndex = slashIndex >= 0 ? slashIndex : 3; // handle missing trailing slash for home page
            return `${origin}/${lang}${urlPath.substring(subIndex)}`;
        }
    }
}

export const linktag = new LinktagService();
